import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { flowRight as compose } from 'lodash';

import { CREATE_SWATCH_REQUEST } from '@/graphql/mutations';
import { getUserCurrentPageType } from 'hooks/useUserCurrentPageType';

import JBImage from 'components/essentials/JBImage';
import CtaButton from 'components/essentials/Cta/CtaButton';
import CtaLink from 'components/essentials/Cta/CtaLink';
import { sendAnalyticsIdentifyEvent } from 'commons/analytics';
import {
  INVALID_NAME_CHARACTERS_REGEX,
  EMAIL_REGEX,
  ZIP_CODE_REGEX,
} from 'commons/constants';
import avo from 'analytics/avo';
import { getUserFromCookie, getTempUserIdCookie } from 'commons/localStorage';
import NewsletterOptIn from 'components/consumer/NewsletterOptIn';
import OptInSmsMarketing from 'components/consumer/OptInSmsMarketing';
import PhoneNumberField, {
  isValidPhoneNumber,
} from 'components/consumer/checkout/desktop/components/Shipping/components/PhoneNumberField';
import SmartyStreetsFloatingField from 'components/essentials/SmartyStreetsFloatingField';
import { streetAddressValidationApi } from 'components/essentials/SmartyStreetsFloatingField/utils';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import LabeledSelectBox from 'components/consumer/LabeledSelectBox';
import isClient from 'commons/isClient';
import formatPhoneE164 from 'commons/formatPhoneE164';
import addDomainToPath from 'commons/addDomainToPath';
import BriteverifyClient from 'commons/britverify-client';
import TwoColumnFormFields from 'components/consumer/TwoColumnFormFields';
import { sendFBEvent, FB_EVENTS } from 'commons/fbEvents';
import { states } from 'commons/usStates';
import FormikFirstErrorFieldFocusOnSubmit from 'components/essentials/FormikFirstErrorFieldFocusOnSubmit';
import withApolloClient from 'components/hoc/withApolloClient';
import withScreen from 'components/hoc/withScreen';

const INITIAL_VALUES = {
  first_name: '',
  last_name: '',
  email: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  newsletter_opt_in: true,
  phone_number: '',
  sms_marketing_opt_in: false,
};

function notEqualTo(ref, msg) {
  return this.test({
    name: 'equalTo',
    exclusive: false,
    message: msg || 'Should not match Address',
    params: {
      reference: ref.path,
    },
    test(value) {
      return value !== this.resolve(ref);
    },
  });
}

Yup.addMethod(Yup.string, 'notEqualTo', notEqualTo);

const formSchema = Yup.object().shape({
  // reCaptchaValue: Yup.string().required('Required'),
  first_name: Yup.string()
    .required('First Name is required')
    .test(
      'test-fname',
      'No invalid characters allowed',
      value => !INVALID_NAME_CHARACTERS_REGEX.test(value)
    ),
  last_name: Yup.string()
    .required('Last Name is required')
    .test(
      'test-lname',
      'No invalid characters allowed',
      value => !INVALID_NAME_CHARACTERS_REGEX.test(value)
    ),
  email: Yup.string()
    .required('Email is required')
    .matches(EMAIL_REGEX, 'Invalid email'),
  address_line1: Yup.string()
    .required('Address is required')
    .min(6, 'Address must contain minimum six characters'),
  address_line2: Yup.string().notEqualTo(Yup.ref('address_line1')),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zip_code: Yup.string()
    .required('Zipcode is required')
    .matches(ZIP_CODE_REGEX, 'Invalid zipcode'),
  // The phone number is optional, so an empty value is valid.
  phone_number: Yup.string().test(
    'valid-phone-if-provided',
    'Please enter a valid phone number',
    value => (value ? isValidPhoneNumber(value) : true)
  ),
});

const validateSwatchRequestForm = async ({ email }) => {
  const validate = await BriteverifyClient.emailLookup(email);
  const errors = {};
  if (!validate?.valid && validate?.message) {
    errors.email = validate?.message;
  }
  return errors;
};

const usStatesList = states.map(state => ({
  label: state.label,
  value: state.abbreviation,
}));

if (isClient()) {
  window.jbEventFBSwatchRequest = () => {
    sendFBEvent(FB_EVENTS.LEAD, {
      content_name: 'SwatchRequest',
    });
  };
}

class SwatchRequestForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      saved: false,
    };
    this.formFieldRefs = {
      first_name: React.createRef(),
      last_name: React.createRef(),
      email: React.createRef(),
      phone_number: React.createRef(),
      address_line1: React.createRef(),
      city: React.createRef(),
      state: React.createRef(),
      zip_code: React.createRef(),
    };
  }

  render() {
    const {
      hideMessageOnSuccess,
      formLocation = 'swatch request, page',
      isMobile,
      inSidebar = false,
    } = this.props;

    if (hideMessageOnSuccess && this.state.saved) {
      return <div data-xc="swatch-submit-success" />;
    }

    if (this.state.saved) {
      return (
        <div
          className={classNames('max-w-[35rem]', {
            'mt-8 mx-auto mb-16 px-5 py-0': !inSidebar,
            'mx-auto my-0 p-0': inSidebar,
          })}
          data-xc="swatch-submit-success"
        >
          <div className="flex items-center justify-center flex-col min-h-[40rem]">
            <h2 className="text-xl font-bold text-gray mx-0 my-3">
              Get excited.
            </h2>
            <div>Your swatches will be on their way soon!</div>
            <br />
            <div className="text-sm text-gray font-normal mt-2 mx-0 mb-0">
              <div>
                Due to COVID-19, we&apos;re taking precautions in the production
                and shipment of swatches. Swatches will be shipped within 14
                business days, but may take a little longer than usual to be
                delivered.
              </div>
            </div>
            <div className="mt-8">
              <CtaLink to="/" styling="solid-button" color="brand" block>
                Continue Browsing
              </CtaLink>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames('max-w-[35rem]', {
          'mt-8 mx-auto mb-16 px-5 py-0': !inSidebar,
          'mx-auto my-0 p-0': inSidebar,
        })}
      >
        <div className="flex items-center">
          <JBImage
            src={addDomainToPath('public_img/swatchbox.png')}
            className="max-h-[30vh] object-cover m-auto"
            width={550}
            trim="color"
            trimPad={20}
          />
        </div>
        <div
          className={classNames('swatch-request-form__content', {
            'px-5 py-0': isMobile,
          })}
        >
          <h2 className="text-xl font-bold text-gray mx-0 my-3">
            <span>Order Free Fabric Swatches</span>
          </h2>

          {formLocation !== 'pdp' && (
            <div className="text-sm text-gray font-normal mt-2 mx-0 mb-0">
              {`Choosing the right fabric or material for your new piece of `}
              <CtaLink
                to="/mid-century-furniture/"
                color="brand"
                hasArrow={false}
              >
                modern furniture
              </CtaLink>
              {` can
              be a challenging decision. Are you considering a few different fabrics
              options? Want to check which fabrics look best in your living space?
              Joybird makes it easier than ever to see and feel how our furniture
              fabrics will fit into your home or apartment. Just complete the short
              form below and we will send you a free fabric swatch sample kit!`}
            </div>
          )}

          <p className="sr-only">* denotes mandatory fields</p>

          <Formik
            initialValues={INITIAL_VALUES}
            validationSchema={formSchema}
            validate={validateSwatchRequestForm}
            onSubmit={async (values, actions) => {
              const formValues = {
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email.trim(),
                address_line1: values.address_line1,
                address_line2: values.address_line2,
                city: values.city,
                state: values.state,
                zip_code: values.zip_code,
                refferal_url: isClient() ? window.document.referrer : '',
                landing_referal: '',
              };

              const validateAddress = await streetAddressValidationApi(
                formValues
              );

              if (validateAddress && validateAddress?.type) {
                actions.setFieldError('formErrors', validateAddress?.message);
                actions.setSubmitting(false);
                this.setState({ saved: false });
                return;
              }
              if (validateAddress?.suggestion) {
                if (
                  formValues.city.trim().toLowerCase() !==
                  validateAddress?.suggestion.city_name.trim().toLowerCase()
                ) {
                  actions.setFieldError(
                    'formErrors',
                    `Invalid city, did you mean ${validateAddress?.suggestion.city_name}?`
                  );
                  actions.setSubmitting(false);
                  this.setState({ saved: false });
                  return;
                }

                if (
                  formValues.state.trim().toLowerCase() !==
                  validateAddress?.suggestion.state_abbreviation
                    .trim()
                    .toLowerCase()
                ) {
                  actions.setFieldError(
                    'formErrors',
                    `Invalid state, did you mean ${validateAddress?.suggestion.state_abbreviation}?`
                  );
                  actions.setSubmitting(false);
                  this.setState({ saved: false });
                  return;
                }

                if (
                  formValues.zip_code.trim().toLowerCase() !==
                  validateAddress?.suggestion.zipcode.trim().toLowerCase()
                ) {
                  actions.setFieldError(
                    'formErrors',
                    `Invalid zip code, did you mean ${validateAddress?.suggestion.zipcode}?`
                  );
                  actions.setSubmitting(false);
                  this.setState({ saved: false });
                  return;
                }
              }

              try {
                actions.setFieldError('formErrors', '');

                const res = await this.props.client.mutate({
                  mutation: CREATE_SWATCH_REQUEST,
                  variables: {
                    create: formValues,
                  },
                });

                if (res.data.swatchRequest.success) {
                  this.setState({ saved: true });

                  if (this.props.onSubmitSuccess) {
                    this.props.onSubmitSuccess();
                  }

                  const userFromCookie = getUserFromCookie();

                  const user = userFromCookie
                    ? {
                        user_id: userFromCookie.id?.toString(),
                        email: userFromCookie.email,
                      }
                    : {
                        email: values.email,
                        user_id:
                          res.data.swatchRequest?.customer_id.toString() ||
                          getTempUserIdCookie(),
                      };
                  const swatchRequestId =
                    res.data.swatchRequest?.request_id || 1;
                  avo.swatchesRequested({
                    userId_: user.user_id?.toString() || '',
                    email: user.email,
                    swatchRequestId: parseInt(swatchRequestId, 10),
                    swatchList: 'All Colors',
                    requestPage: window ? window.location.pathname : 'N/A',
                    shippingAddress: {
                      addressLine1: values.address_line1,
                      addressLine2: values.address_line2,
                      city: values.city,
                      state: values.state,
                      zip: values.zip_code,
                      country: 'USA',
                    },
                    backendEmail: user.email,
                  });
                  sendAnalyticsIdentifyEvent(user?.user_id, {
                    first_name: values.first_name,
                    last_name: values.last_name,
                    email: values.email,
                  });

                  const phone = formatPhoneE164(values.phone_number);
                  avo.emailAcquired({
                    userId_: user?.user_id,
                    email: user.email,
                    location: getUserCurrentPageType(this.props?.match),
                    optedInMarketing: values.newsletter_opt_in,
                    backendEmail: user.email,
                    component:
                      this.props?.match.path === '/samples'
                        ? avo.Component.SWATCH_REQUEST
                        : avo.Component.SIDE_PANEL,
                    talkativeCustomerId: '',
                    optedInSmsMarketing: phone
                      ? !!values.sms_marketing_opt_in
                      : false,
                    phone,
                  });
                } else {
                  actions.setFieldError(
                    'formErrors',
                    res.data.swatchRequest.message || 'Unable to order swatches'
                  );
                }
              } catch (e) {
                actions.setFieldError(
                  'formErrors',
                  'Unable to submit your swatch request. Please try again.'
                );
              } finally {
                actions.setSubmitting(false);
              }
            }}
            render={({
              values,
              errors,
              touched,
              handleSubmit,
              handleBlur,
              handleChange,
              setFieldValue,
              setFieldTouched,
              isSubmitting,
            }) => {
              const enableCityStateFields = true;

              return (
                <form onSubmit={handleSubmit}>
                  <TwoColumnFormFields className="[&>*]:last:lg:mr-0">
                    <LabeledTextInput
                      label="First Name *"
                      name="first_name"
                      autoComplete="given-name"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.first_name}
                      data-xc="first_name-field"
                      errorMessage={
                        touched.first_name && errors.first_name
                          ? errors.first_name
                          : undefined
                      }
                      ref={this.formFieldRefs.first_name}
                    />

                    <LabeledTextInput
                      label="Last Name *"
                      name="last_name"
                      autoComplete="family-name"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.last_name}
                      data-xc="last_name-field"
                      errorMessage={
                        touched.last_name && errors.last_name
                          ? errors.last_name
                          : undefined
                      }
                      ref={this.formFieldRefs.last_name}
                    />
                  </TwoColumnFormFields>

                  <LabeledTextInput
                    name="email"
                    type="text"
                    autoComplete="email"
                    label="E-mail *"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    data-xc="email-field"
                    errorMessage={
                      touched.email && errors.email ? errors.email : undefined
                    }
                    ref={this.formFieldRefs.email}
                  />

                  <PhoneNumberField
                    id="phone_number"
                    name="phone_number"
                    placeholder="Phone (Optional)"
                    data-xc="phone_number-field"
                    value={values.phone_number}
                    error={errors.phone_number}
                    isTouched={touched.phone_number}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    isRequired={false}
                    ref={this.formFieldRefs.phone_number}
                  />

                  <div className="swatch-request-form__field swatch-request-form__field-address">
                    <SmartyStreetsFloatingField
                      type="text"
                      name="address_line1"
                      placeholder="Address *"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.address_line1}
                      data-xc="address_line1-field"
                      setFieldValue={setFieldValue}
                      addressFieldName="address_line1"
                      zipCodeFieldName="zip_code"
                      refField={this.formFieldRefs.address_line1}
                      error={
                        touched.address_line1 && errors.address_line1
                          ? errors.address_line1
                          : undefined
                      }
                    />
                  </div>

                  {enableCityStateFields && (
                    <>
                      <TwoColumnFormFields>
                        <LabeledTextInput
                          label="Apartment / Suite #"
                          name="address_line2"
                          autoComplete="address-line2"
                          type="text"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.address_line2}
                          data-xc="address_line2-field"
                          errorMessage={
                            touched.address_line2 && errors.address_line2
                              ? errors.address_line2
                              : undefined
                          }
                        />

                        <LabeledTextInput
                          label="City *"
                          name="city"
                          autoComplete="address-level2"
                          type="text"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.city}
                          data-xc="city-field"
                          errorMessage={
                            touched.city && errors.city
                              ? errors.city
                              : undefined
                          }
                          ref={this.formFieldRefs.city}
                        />
                      </TwoColumnFormFields>

                      <TwoColumnFormFields>
                        <LabeledSelectBox
                          label="State *"
                          name="state"
                          autoComplete="address-level1"
                          options={usStatesList}
                          data-xc="state-field"
                          onChange={event => {
                            setFieldValue('state', event.target.value);
                            setFieldTouched('state');
                          }}
                          onBlur={handleBlur}
                          value={values.state}
                          errorMessage={
                            touched.state && errors.state
                              ? errors.state
                              : undefined
                          }
                          ref={this.formFieldRefs.state}
                        />

                        <LabeledTextInput
                          name="zip_code"
                          type="text"
                          autoComplete="postal-code"
                          label="Zip code *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.zip_code}
                          data-xc="zip_code-field"
                          errorMessage={
                            touched.zip_code && errors.zip_code
                              ? errors.zip_code
                              : undefined
                          }
                          ref={this.formFieldRefs.zip_code}
                        />
                      </TwoColumnFormFields>
                    </>
                  )}

                  {errors.formErrors && (
                    <div className="bg-brand-lightest text-sm text-center p-4 text-brand mb-2">
                      {errors.formErrors.match(/address is invalid/gi)
                        ? 'Invalid address, please correct your address and resubmit. If this address is correct, please call Customer Service to order your swatches at (888) 282-0842'
                        : errors.formErrors}
                    </div>
                  )}

                  <div
                    data-xc="swatch-request-__newsletter_opt_in"
                    className="pt-2"
                  >
                    <NewsletterOptIn
                      name="newsletter_opt_in"
                      onClick={isChecked => {
                        setFieldValue('newsletter_opt_in', isChecked);
                      }}
                      checked={values.newsletter_opt_in}
                    />
                    <OptInSmsMarketing
                      name="sms_marketing_opt_in"
                      onClick={isChecked => {
                        setFieldValue('sms_marketing_opt_in', isChecked);
                      }}
                      checked={values.sms_marketing_opt_in}
                    />
                  </div>

                  <div data-xc="swatch-request-submit" className="pt-2">
                    <CtaButton
                      color="black"
                      styling="solid-button"
                      data-xc="signup-button"
                      block
                      type="submit"
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                    >
                      Send My Free Samples
                    </CtaButton>
                  </div>
                  <FormikFirstErrorFieldFocusOnSubmit
                    formFieldRefs={this.formFieldRefs}
                  />
                </form>
              );
            }}
          />
        </div>
      </div>
    );
  }
}

SwatchRequestForm.propTypes = {
  client: PropTypes.object,
  formLocation: PropTypes.string,
  hideMessageOnSuccess: PropTypes.bool,
  inSidebar: PropTypes.bool,
  isMobile: PropTypes.bool,
  match: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
};

export default compose(
  withApolloClient,
  withRouter,
  withScreen
)(SwatchRequestForm);
